import { useContext, useReducer } from 'react'
import { GroupContext, GroupReducer, GroupState, initState } from './reducer'

// TYPES
import type { IToast } from '@components/molecules/toast'

// UTILITIES
import * as GroupActions from './actions'


/**
 * Instantiates a new `Group` store and returns the state and dispatch in an object for use in an `GroupContext.Provider`.\
 * Only one Reducer is needed per Provider, and as such, this hook should be used as close to it's designated Provider as possible.\
 * ### Use the `useGroupState` and `useGroupDispatch` hooks to access Reducer data.
 * 
 * @returns A pre-formatted object an GroupProvider will accept as a value containing the Reducer's State and Dispatch.
 */
export const useGroupReducer = () => {
    const [localState, localDispatch] = useReducer(GroupReducer, initState)
    return { localState, localDispatch }
}

/**
 * Access the nearest `Group` Provider and returns the full state.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns The full `GroupState` object.
 */
export const useGroupState = () => {
    const localState = useContext(GroupContext).localState
    if (!localState) throw new Error('useGroupState must be used within an GroupContext Provider')
    return localState
}

/**
 * Access the nearest `Group` Provider and returns it's Dispatch method.
 * As a safe guard, an error is thrown if a Provider is not found.
 * 
 * @returns A Dispatch method that accepts `GroupActionTypes` for the purposes of updating the Group state.
 */
export const useGroupDispatch = () => {
    const localDispatch = useContext(GroupContext).localDispatch
    if (!localDispatch) throw new Error('useGroupDispatch must be used within an GroupContext Provider')
    return localDispatch
}


type GroupSettings = () => {
    /**
     * A quick way of appending data to the `toast` variable in the Auth store.
     *
     * @param props Accepts either a `string` or `IToast` arguments. By default, if typeof string, the Toast displayed will be of type error.
     * @returns 
     */
    createToast: (props: string | IToast) => void

    /**
     * Controls the `requestBody` variable in the Group store.
     * 
     * @param body Used to create a shallow copy with the `requestBody` stored in the reducer. Only values provided will be overwritten.
     * @returns 
     */
    updateRequestBody: (body: Partial<CreateGroupRequestBody>) => void

    /**
     * Resets the Group state in the Group store.
     */
    resetGroupState: () => void

    /**
     * A copy of the state in the local GroupReducer.
     */
    state: GroupState
}

/**
 * Returns an object of pre-formatted utilities that control certain aspects of the Group process.
 */
export const useGroupSettings: GroupSettings = () => {
    const state = useGroupState()
    const { } = state
    const localDispatch = useGroupDispatch()

    function createToast(props: string | IToast) {
        localDispatch(GroupActions.setToast(typeof props === 'string' ? {
            type: 'error',
            text: props,
        } : props))
    }

    function updateRequestBody(body: Partial<CreateGroupRequestBody>) {
        localDispatch(GroupActions.updateRequestBody(body))
    }

    function resetGroupState() {
        localDispatch(GroupActions.resetGroupState())
    }

    return { createToast, updateRequestBody, resetGroupState, state }
}